import { Button, Flex, Text, SimpleGrid, Checkbox } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useFormik } from "formik";
import { FormWrapper } from "@/components/FormWrapper/FormWrapper";
import { BaseInput } from "@/components/Controls/BaseInput";
import { BaseSelect, SelectOption } from "@/components/Controls/BaseSelect";
import { mockPartners } from "@/pages/PartnersPage/mockData";
import { useMemo } from "react";

export interface LeadContactForm {
  name: string;
  position: string;
  work_phone: string;
  work_email: string;
  telegram: string;
  social_network: string;
  partner_id: number | null;
  leads: string[];
}

export const leadsOptions = [
  "Разработка корпоративного сайта",
  "Мобильное приложение для доставки",
  "Автоматизация бизнес-процессов",
  "Редизайн интернет-магазина",
  "Система управления складом",
  "CRM система для отдела продаж",
  "Разработка API для интеграций",
  "Портал для сотрудников",
  "Система аналитики и отчетности",
  "E-commerce платформа",
  "Консультация по цифровизации",
  "Мобильный банкинг",
];

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  
  if (digits.startsWith("7")) {
    const phoneDigits = digits.slice(1);
    if (phoneDigits.length === 0) return "+7 (";
    if (phoneDigits.length <= 3) return `+7 (${phoneDigits}`;
    if (phoneDigits.length <= 6) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
    if (phoneDigits.length <= 8) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
    return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 8)}-${phoneDigits.slice(8, 10)}`;
  }
  
  if (digits.length <= 1) return `+7 (${digits}`;
  if (digits.length <= 4) return `+7 (${digits}`;
  if (digits.length <= 7) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 9) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
};

export const CreateLeadContactPage = () => {
  const navigate = useNavigate();

  const partnerOptions: SelectOption[] = useMemo(() => {
    return mockPartners.map((partner) => ({
      value: partner.id,
      label: partner.name,
    }));
  }, []);

  const formik = useFormik<LeadContactForm>({
    initialValues: {
      name: "",
      position: "",
      work_phone: "",
      work_email: "",
      telegram: "",
      social_network: "",
      partner_id: null,
      leads: [],
    },
    onSubmit: () => {},
  });

  const handleLeadChange = (lead: string, isChecked: boolean) => {
    if (isChecked) {
      formik.setFieldValue("leads", [...formik.values.leads, lead]);
    } else {
      formik.setFieldValue("leads", formik.values.leads.filter((l) => l !== lead));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    formik.setFieldValue("work_phone", formatted);
  };

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate("/leads_contacts")} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />Назад к списку
        </Text>
        <Flex flexDirection="column" gap="4px">
          <Text fontSize={30} lineHeight="36px" fontWeight={700} color="#3b515e">
            Создание контакта
          </Text>
          <Text fontSize={16} lineHeight="24px" fontWeight={400} color="#65758b">
            Заполните информацию о новом контакте
          </Text>
        </Flex>

        <FormWrapper>
          <Flex flexDirection="column" gap="24px">
            <Text fontSize={18} fontWeight={600} color="#3b515e">
              Основная информация
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <BaseInput
                fieldTitle="Имя"
                requiredField={true}
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Введите имя"
                name="name"
              />

              <BaseInput
                fieldTitle="Должность"
                requiredField={true}
                value={formik.values.position}
                onChange={formik.handleChange}
                placeholder="Введите должность"
                name="position"
              />

              <BaseInput
                fieldTitle="Рабочий телефон"
                value={formik.values.work_phone}
                onChange={handlePhoneChange}
                placeholder="+7 (__) __-__-__"
                name="work_phone"
              />

              <BaseInput
                fieldTitle="Рабочая почта"
                value={formik.values.work_email}
                onChange={formik.handleChange}
                placeholder="email@example.com"
                name="work_email"
                type="email"
              />

              <BaseInput
                fieldTitle="Telegram"
                value={formik.values.telegram}
                onChange={formik.handleChange}
                placeholder="@username"
                name="telegram"
              />

              <BaseInput
                fieldTitle="Соц. сеть"
                value={formik.values.social_network}
                onChange={formik.handleChange}
                placeholder="https://linkedin.com/in/username"
                name="social_network"
              />
            </SimpleGrid>

            <BaseSelect
              fieldTitle="Партнер"
              options={partnerOptions}
              placeholder="Выберите партнера"
              value={formik.values.partner_id}
              onChange={(value) => formik.setFieldValue("partner_id", value)}
            />

            <Text fontSize={18} fontWeight={600} color="#3b515e">
              Лиды
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {leadsOptions.map((lead) => (
                <Checkbox
                  key={lead}
                  isChecked={formik.values.leads.includes(lead)}
                  onChange={(e) => handleLeadChange(lead, e.target.checked)}
                  borderColor="#AAB0C1"
                  colorScheme="blue"
                >
                  {lead}
                </Checkbox>
              ))}
            </SimpleGrid>
          </Flex>
        </FormWrapper>

        <Flex gap="16px" justifyContent="flex-end">
          <Button onClick={() => navigate("/leads_contacts")}>Отмена</Button>
          <Button
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
            onClick={() => {
              console.log("Создание контакта:", formik.values);
            }}
            isDisabled={!formik.values.name || !formik.values.position}
          >
            Создать контакт
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};

